import React from "react";
import { Button } from "../ui/button";

/**
 * Back / next / submit controls for a survey page.
 *
 * @param {Object} props
 * @param {boolean} props.canBack
 * @param {boolean} props.isLast
 * @param {boolean} [props.allowBack]
 * @param {boolean} [props.readOnly]
 * @param {"draft"|"edit"|"readonly"} [props.mode]
 * @param {boolean} [props.saving]
 * @param {number} [props.pageIndex]
 * @param {number} [props.pageCount]
 * @param {() => void} props.onBack
 * @param {() => void} props.onNext
 * @param {() => void} [props.onSubmit]
 * @param {() => void} [props.onDone]
 * @param {string} [props.submitLabel]
 * @returns {JSX.Element}
 */
export default function SurveyNav({
  canBack,
  isLast,
  allowBack = true,
  readOnly = false,
  mode = "draft",
  saving = false,
  pageIndex = 0,
  pageCount = 1,
  onBack,
  onNext,
  onSubmit,
  onDone,
  submitLabel,
}) {
  const showBack = allowBack && canBack;
  const showPage = pageCount > 1;
  const pageNumber = pageIndex + 1;
  let primaryLabel = "Next";
  let primaryDisabled = false;
  let primaryAction = onNext;
  let showPrimary = true;
  if (readOnly || mode === "readonly") {
    if (isLast) {
      if (onDone) {
        primaryLabel = "Done";
        primaryAction = onDone;
      } else {
        showPrimary = false;
      }
    }
  } else if (isLast && mode === "edit") {
    primaryLabel = "Done";
    primaryAction = onDone;
  } else if (isLast && mode === "draft") {
    primaryLabel = submitLabel || "Submit";
    primaryDisabled = saving;
    primaryAction = onSubmit;
  }
  if (saving && !readOnly && mode !== "readonly") {
    primaryDisabled = true;
  }

  if (!showBack && !showPage && !showPrimary) {
    return null;
  }

  return (
    <div className="mt-8 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
      <div>
        {showBack ? (
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
        ) : null}
      </div>
      {showPage ? (
        <p
          className="text-xs tabular-nums text-text-muted"
          aria-label={`Page ${pageNumber} of ${pageCount}`}
        >
          {pageNumber} / {pageCount}
        </p>
      ) : (
        <span />
      )}
      <div className="justify-self-end">
        {showPrimary ? (
          <Button type="button" onClick={primaryAction} disabled={primaryDisabled}>
            {primaryLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
